// CSS
import './PricingNew.css';
// React hooks
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Skybase hooks
import { useAuth } from '../../context/AuthContext';
import { useBilling } from '../../context/BillingContext';
// Skybase components
import NavBarNew from '../../components/NavBarNew/NavBarNew';
import FooterNew from '../../components/FooterNew/FooterNew';
// External hooks/components
import { getToken } from '@clerk/react';
// Media assets
import AnimatedHorizontalLogo from '../../assets/videos/skybase-framer-logo.webm';
import InformationalPopover from '../../components/Popovers/InformationalPopover/InformationalPopover';


// ==========
// PricingNew – represents the '/pricing' Landing page.
// ==========
function PricingNew() {

    // destructure global context
    const { startCheckoutSession, startBillingPortalSession } = useBilling();
    const { isSubscribed, checkIsSubscribed } = useAuth();

    // init hooks
    const navigate = useNavigate('');

    // state vars
    const [pricingMode, setPricingMode] = useState('monthly');

    
    // ===============================
    // Handle Pricing Button onClick() – Either (A) redirects to '/create-account' or (B) redirects to a unique Stripe-Checkout-Session-URL
    // ===============================
    async function handlePricingButtonClick() {

        // Edge case: NO <Clerk JWT> --> immediately re-direct to '/create-account'
        const token = await getToken();
        if (!token) {
            navigate('/create-account', {state: { from: location.pathname }});
            return;
        }

        // Get the Postgres CurrentUser's 'isSubscribed' status
        await checkIsSubscribed();

        // Condition #1: CurrentUser !isSubscribed --> immediately re-direct to a Stripe-Checkout-Session-URL
        if (!isSubscribed) {
            
            // set the proper Stripe Price Lookup Key
            const stripe_price_lookup_key = (pricingMode === 'monthly') ? 'unlimited_monthly' : 'unlimited_yearly';

            // Run startCheckoutSession() --> get a Stripe-Checkout-Session-URL
            const StripeCheckoutSessionURL = await startCheckoutSession(stripe_price_lookup_key);

            // Open the Stripe-Checkout-Session-URL in THIS window.
            window.location = StripeCheckoutSessionURL;
        } 
        
        // Condition #2: CurrentUser isSubscribed --> immediately re-direct to a Stripe-Billing-Portal-URL
        else {
            
            // Run startPortalBillingSession() --> get a Stripe-Billing-Portal-URL
            const StripeBillingPortalURL = await startBillingPortalSession();

            // Open the Stripe-Billing-Portal-URL in THIS window.
            window.location = StripeBillingPortalURL;
        }
    }


    // ==========
    // Edge case: Display <InformationalPopover/> IFF successful StripeCheckoutSession completion.
    // ==========
    // Define vars
    const location = useLocation();
    const [isInformationalPopoverOpen, setIsInformationalPopoverOpen] = useState(false);
    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get("checkout");

    // Trigger the InformationalPopover for checkoutStatus changes.
    useEffect(() => {
        if (checkoutStatus === "success"){
            // Open the <InformationalPopover/> if successful StripeCheckoutSession
            setIsInformationalPopoverOpen(true);

            // Change the browser URL without reloading the page. Replaces the current URL to prevent re-opening the one-time Confirmation <InformationalPopover/>.
            navigate("/pricing", { replace: true });
        }
    }, [checkoutStatus, navigate])
  


    // JSX Component
    return(
        <div className='PricingNewPage'>
            
            {/* Aesthetic background */}
            <div className='PricingNewPage-aesthetic-bg'></div>

            {/* Navigation Bar */}
            <NavBarNew/>

            {/* Main content */}
            <div className='PricingNewPage-main-content'>

                {/* Pricing mode */}
                <div className='PricingNewPage-pricing-mode'>
                    
                    {/* Monthly */}
                    <div className={`PricingNewPage-pricing-mode-button ${pricingMode === 'monthly' ? 'enabled' : ''}`} onClick={() => setPricingMode('monthly')}>Monthly</div>

                    {/* yearly */}
                    <div className={`PricingNewPage-pricing-mode-button ${pricingMode === 'yearly' ? 'enabled' : ''}`} onClick={() => setPricingMode('yearly')}>Yearly</div>

                </div>

                {/* Pricing card */}
                <div className='PricingNewPage-pricing-card SearchPage-fade-in'>
                    
                    {/* Card title */}
                    <div className='PricingNewPage-pricing-card-title'>Unlimited { pricingMode === 'monthly' ? 'Monthly' : 'Yearly' } Subscription</div>

                    {/* Pricing row */}
                    <div className='PricingNewPage-pricing-card-pricing-row'>
                        
                        {/* Price */}
                        <div className='PricingNewPage-pricing-card-pricing-row--price'>
                            
                            {/* Monetary sign */}
                            <div className='PricingNewPage-pricing-card-pricing-row--monetary-sign'>$</div>
                            
                            {/* Numerical price (USD) */}
                            { pricingMode === 'monthly' ? 20.00 : 200.00 }

                        </div>

                        {/* Currency */}
                        <div className='PricingNewPage-pricing-card-pricing-row--currency'>
                            USD /<br />{ pricingMode === 'monthly' ? 'month' : 'year'}
                        </div>

                        {/* Discount chip (monthly) */}
                        { pricingMode === 'monthly' &&  <div className='PricingNewPage-pricing-card-pricing-row-discount-chip'>Most popular</div> }

                        {/* Discount chip (yearly) */}
                        { pricingMode === 'yearly' && <div className='PricingNewPage-pricing-card-pricing-row-discount-chip'>15% discount</div> }
                    
                    </div>

                    {/* Tagline */}
                    <div className='PricingNewPage-pricing-card-tagline'>One price. Every database. No usage limits.</div>

                    {/* Feature bullets */}
                    <div className='PricingNewPage-pricing-card-features'>
                        <div>• Unlimited access to all Skybase databases.</div>
                        <div>• All platform features.</div>
                        <div>• Unlimited searches.</div>
                        <div>• Early access to all future updates.</div>
                        <div>• Priority 24/7 customer support.</div>
                    </div>

                    {/* Card CTA Button */}
                    <div className={`PricingNewPage-pricing-card-cta-button ${isSubscribed ? 'blue' : ''}`} onClick={() => handlePricingButtonClick() }>{isSubscribed ? "Manage subscription ✓" : "Purchase Plan"}</div>

                </div>
            </div>

            {/* Footer */}
            <FooterNew/>

            {/* InformationalPopover */}
            { isInformationalPopoverOpen &&
                <InformationalPopover
                    titleText={"Subscription Activated"}
                    bodyText={"Thank you for purchasing a Skybase subscription plan. You now have unlimited access to all platform features and databases."}
                    primaryButtonText={"Start Searching Now"}
                    primaryButtonCallback={() => navigate('../library')}
                    secondaryButtonText={"Close"}
                    secondaryButtonCallback={() => setIsInformationalPopoverOpen(false)}
                />
            }

        </div>
    )
};


// Export statement
export default PricingNew;